import React, { useState } from 'react'
import {
  Button,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  TextField,
  GlobalStyles
} from '@mui/material'

import { TransitionGroup, CSSTransition } from 'react-transition-group'

type GenericTransferElementsProps<T> = {
  availableItems: T[]
  items: T[]
  getKey: (item: T) => number
  getLabel: (item: T) => string
  leftTitle: string
  rightTitle: string
  onSave?: (keys: number[]) => void //permite obtener los elementos de la derecha, al presionar el boton
  buttonTittle?: string
  onRightItemsChange?: (rightItems: T[]) => void // permite obtener los elementos de la derecha
}

const GenericTransferElements = <T,>({
  availableItems,
  items,
  getKey,
  getLabel,
  leftTitle,
  rightTitle,
  onSave,
  buttonTittle,
  onRightItemsChange
}: GenericTransferElementsProps<T>) => {
  const [rightItems, setRightItems] = useState<T[]>(items)
  const [leftItems, setLeftItems] = useState<T[]>(availableItems)
  const [selectedLeft, setSelectedLeft] = useState<Set<number>>(new Set())
  const [selectedRight, setSelectedRight] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState<string>('')

  const handleToggle = (
    id: number,
    setSelected: React.Dispatch<React.SetStateAction<Set<number>>>
  ) => {
    setSelected((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleMoveToRight = () => {
    const itemsToMove = leftItems.filter((item) =>
      selectedLeft.has(getKey(item))
    )

    if(onRightItemsChange){
      setRightItems((prev) => {
        const updated = [...prev, ...itemsToMove]
        onRightItemsChange?.(updated) // Llamar al callback
        return updated
      })
    }else{
      setRightItems((prev) => [...prev, ...itemsToMove])
    }
    setLeftItems((prev) =>
      prev.filter((item) => !selectedLeft.has(getKey(item)))
    )
    setSelectedLeft(new Set())
  }

  const handleMoveToLeft = () => {
    const itemsToMove = rightItems.filter((item) =>
      selectedRight.has(getKey(item))
    )
    setLeftItems((prev) => [...prev, ...itemsToMove])
    
    if(onRightItemsChange){
      setRightItems((prev) => {
        const updated = prev.filter((item) => !selectedRight.has(getKey(item)))
        onRightItemsChange?.(updated) // Llamar al callback
        return updated
      })
    }else{
      setRightItems((prev) =>
        prev.filter((item) => !selectedRight.has(getKey(item)))
      )
    }

    setSelectedRight(new Set())
  }

  const handleSave = () => {
    const keys = rightItems.map(getKey)
    if(onSave)
      onSave(keys)
  }

  const renderList = (
    items: T[],
    selected: Set<number>,
    toggle: (id: number) => void
  ) => {
    if (items.length === 0) {
      return (
        <Typography variant="body2" align="center" color="textSecondary">
          No hay resultados
        </Typography>
      )
    }

    return (
      <TransitionGroup>
        {items.map((item) => (
          <CSSTransition
            key={getKey(item)}
            timeout={300}
            classNames={{
              enter: 'transfer-enter',
              enterActive: 'transfer-enter-active',
              exit: 'transfer-exit',
              exitActive: 'transfer-exit-active'
            }}
          >
            <ListItemButton
              onClick={() => toggle(getKey(item))}
              selected={selected.has(getKey(item))}
              sx={{
                border: '2px solid #ccc',
                margin: '4px 0',
                cursor: 'pointer',
                transition: 'all 300ms ease',
                height: '30px',
                borderRadius: '6px',
                backgroundColor: selected.has(getKey(item))
                  ? '#3f51b5'
                  : 'white',
                color: selected.has(getKey(item)) ? 'white' : 'black'
              }}
            >
              <ListItemText primary={getLabel(item)} />
            </ListItemButton>
          </CSSTransition>
        ))}
      </TransitionGroup>
    )
  }

  const filteredLeftItems = leftItems.filter((item) =>
    getLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
  ) // Filtra los elementos por el término de búsqueda

  return (
    <Grid
      container
      spacing={2}
      justifyContent='center'
      alignItems='stretch'
      style={{ height: '100%' }}
    >
      <GlobalStyles
        styles={{
          '.transfer-enter': { opacity: 0 },
          '.transfer-enter-active': {
            opacity: 1,
            transition: 'opacity 300ms ease-in'
          },
          '.transfer-exit': { opacity: 1 },
          '.transfer-exit-active': {
            opacity: 0,
            transition: 'opacity 300ms ease-out'
          }
        }}
      />
      <Grid item xs={4} sx={{ height: '100%', overflowY: 'auto', transition: 'height 0.3s ease' }}>
        <Typography variant="h6" align="center">{leftTitle}</Typography>
        <TextField
          variant="outlined"
          placeholder="Buscar..."
          fullWidth
          margin="dense"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Box
          sx={{
            maxHeight: '200px',
            overflow: 'auto',
            border: '1px solid #ccc',
            p: '6px',
            borderRadius: '2px'
          }}
        >
          <List>
            {renderList(filteredLeftItems, selectedLeft, (id) =>
              handleToggle(id, setSelectedLeft)
            )}
          </List>
        </Box>
      </Grid>
      <Grid item>
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          style={{ height: '100%' }}
        >
          <Button
            variant='contained'
            color='primary'
            onClick={handleMoveToRight}
            style={{ marginBottom: '8px' }}
            disabled={selectedLeft.size === 0}
          >
            &gt;
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleMoveToLeft}
            style={{ marginBottom: '8px' }}
            disabled={selectedRight.size === 0}
          >
            &lt;
          </Button>
        </Box>
      </Grid>
      <Grid
        item
        xs={4}
        sx={{ height: '100%', overflowY: 'auto', transition: 'height 0.3s ease' }}
      >
        <Typography variant="h6" align="center">{rightTitle}</Typography>
        <Box
          sx={{
            maxHeight: '200px',
            overflow: 'auto',
            border: '1px solid #ccc',
            p: '6px',
            borderRadius: '2px'
          }}
        >
          <List>
            {renderList(rightItems, selectedRight, (id) =>
              handleToggle(id, setSelectedRight)
            )}
          </List>
        </Box>
      </Grid>
      {/* si viene onSave, muestra el boton */}
      {onSave && 
        <Grid item xs={3}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          disabled={rightItems.length === 0}
          fullWidth
        >
           {buttonTittle ? buttonTittle: 'Guardar'}
        </Button>
        </Grid>
      }
    </Grid>
      

  )
}

export default GenericTransferElements
